/**
 * Schema definitions for all document types supported by Document Extract API
 * Each schema maps extraction keys to field types
 */

import { DocumentExtractConfig } from './document-extract.model';
import {
  CANADA_PROVINCES,
  USA_PROVINCES,
  LICENSE_CLASSES_ALL,
  ISSUING_COUNTRIES,
  VISA_TYPES,
  TRAINING_CERTIFICATE_NAMES,
  DOCUMENT_SOURCES,
  TEST_RESULTS,
  BOOLEAN_OPTIONS
} from './document-extract-options';

/**
 * Complete registry of all document extraction schemas
 */
export const DOCUMENT_EXTRACT_SCHEMAS: Record<string, DocumentExtractConfig> = {
  // ==========================================
  // Driver Documents (Registration/Profile)
  // ==========================================
  
  DriverLicense: {
    documentType: 'DriverLicense',
    displayName: 'Driver License',
    schema: {
      license_number: 'string',
      issue_date: 'date',
      expiry_date: 'date',
      issuing_province: 'select',
      issuing_country: 'select',
      license_type: 'select',
      full_name: 'string',
      date_of_birth: 'date',
      gender: 'select',
      // Address fields - complete set for profile update
      full_address: 'string',
      address_street: 'string',
      address_city: 'string',
      address_subdivision: 'string',
      address_postal_code: 'string',
      address_country: 'string',
      restrictions: 'string',
      endorsements: 'string'
    },
    dropdownOptions: {
      issuing_province: [...CANADA_PROVINCES.map(p => p.value), ...USA_PROVINCES.map(p => p.value)],
      issuing_country: ISSUING_COUNTRIES.map(c => c.value),
      license_type: LICENSE_CLASSES_ALL,
      gender: ['male', 'female']
    },
    sourceAuthority: DOCUMENT_SOURCES.DriverLicense
  },

  PRCard: {
    documentType: 'PRCard',
    displayName: 'PR Card',
    schema: {
      card_number: 'string',
      issue_date: 'date',
      expiry_date: 'date',
      full_name: 'string',
      date_of_birth: 'date',
      country_of_citizenship: 'string',
      document_number: 'string'
    },
    sourceAuthority: DOCUMENT_SOURCES.PRCard
  },

  Passport: {
    documentType: 'Passport',
    displayName: 'Passport',
    schema: {
      passport_number: 'string',
      issue_date: 'date',
      expiry_date: 'date',
      issuing_country: 'select',
      full_name: 'string',
      date_of_birth: 'date',
      place_of_birth: 'string',
      nationality: 'string'
    },
    dropdownOptions: {
      issuing_country: ISSUING_COUNTRIES.map(c => c.value)
    },
    sourceAuthority: DOCUMENT_SOURCES.Passport
  },

  CanadaVisa: {
    documentType: 'CanadaVisa',
    displayName: 'Canada Visa',
    schema: {
      visa_number: 'string',
      issue_date: 'date',
      expiry_date: 'date',
      visa_type: 'select',
      full_name: 'string',
      date_of_birth: 'date',
      passport_number: 'string'
    },
    dropdownOptions: {
      visa_type: VISA_TYPES
    },
    sourceAuthority: DOCUMENT_SOURCES.CanadaVisa
  },

  USVisa: {
    documentType: 'USVisa',
    displayName: 'US Visa',
    schema: {
      visa_number: 'string',
      issue_date: 'date',
      expiry_date: 'date',
      visa_type: 'select',
      full_name: 'string',
      date_of_birth: 'date',
      passport_number: 'string',
      visa_class: 'string'
    },
    dropdownOptions: {
      visa_type: VISA_TYPES
    },
    sourceAuthority: DOCUMENT_SOURCES.USVisa
  },

  WorkPermit: {
    documentType: 'WorkPermit',
    displayName: 'Work Permit',
    schema: {
      permit_number: 'string',
      issue_date: 'date',
      expiry_date: 'date',
      employer_name: 'string',
      full_name: 'string',
      date_of_birth: 'date',
      occupation: 'string',
      work_location: 'string'
    },
    sourceAuthority: DOCUMENT_SOURCES.WorkPermit
  },

  FastCard: {
    documentType: 'FastCard',
    displayName: 'FAST Card',
    schema: {
      fast_card_number: 'string',
      issue_date: 'date',
      expiry_date: 'date',
      full_name: 'string',
      date_of_birth: 'date',
      membership_number: 'string'
    },
    sourceAuthority: DOCUMENT_SOURCES.FastCard
  },

  HealthCard: {
    documentType: 'HealthCard',
    displayName: 'Health Card',
    schema: {
      health_card_number: 'string',
      issue_date: 'date',
      expiry_date: 'date',
      issuing_province: 'select',
      full_name: 'string',
      date_of_birth: 'date',
      version_code: 'string'
    },
    dropdownOptions: {
      issuing_province: CANADA_PROVINCES.map(p => p.value)
    },
    sourceAuthority: DOCUMENT_SOURCES.HealthCard
  },

  // ==========================================
  // Training Certificates
  // ==========================================

  EmergencyResponseTraining: {
    documentType: 'EmergencyResponseTraining',
    displayName: 'Emergency Response Training',
    schema: {
      certification_name: 'string',
      issuing_organization: 'string',
      issue_date: 'date',
      expiry_date: 'date',
      credential_id: 'string',
      credential_url: 'string',
      participant_name: 'string'
    },
    sourceAuthority: DOCUMENT_SOURCES.EmergencyResponseTraining
  },

  FatigueManagementTraining: {
    documentType: 'FatigueManagementTraining',
    displayName: 'Fatigue Management Training',
    schema: {
      certification_name: 'string',
      issuing_organization: 'string',
      issue_date: 'date',
      expiry_date: 'date',
      credential_id: 'string',
      credential_url: 'string',
      participant_name: 'string'
    },
    sourceAuthority: DOCUMENT_SOURCES.FatigueManagementTraining
  },

  HazardousMaterialsEndorsement: {
    documentType: 'HazardousMaterialsEndorsement',
    displayName: 'Hazardous Materials Endorsement',
    schema: {
      certification_name: 'string',
      issuing_organization: 'string',
      issue_date: 'date',
      expiry_date: 'date',
      credential_id: 'string',
      credential_url: 'string',
      participant_name: 'string',
      endorsement_number: 'string'
    },
    sourceAuthority: DOCUMENT_SOURCES.HazardousMaterialsEndorsement
  },

  HoursOfService: {
    documentType: 'HoursOfService',
    displayName: 'Hours Of Service',
    schema: {
      certification_name: 'string',
      issuing_organization: 'string',
      issue_date: 'date',
      expiry_date: 'date',
      credential_id: 'string',
      credential_url: 'string',
      participant_name: 'string'
    },
    sourceAuthority: DOCUMENT_SOURCES.HoursOfService
  },

  MandatoryEntryLevelTraining: {
    documentType: 'MandatoryEntryLevelTraining',
    displayName: 'Mandatory Entry Level Training',
    schema: {
      certification_name: 'string',
      issuing_organization: 'string',
      issue_date: 'date',
      expiry_date: 'date',
      credential_id: 'string',
      credential_url: 'string',
      participant_name: 'string',
      training_provider_number: 'string'
    },
    sourceAuthority: DOCUMENT_SOURCES.MandatoryEntryLevelTraining
  },

  MountainDriving: {
    documentType: 'MountainDriving',
    displayName: 'Mountain Driving',
    schema: {
      certification_name: 'string',
      issuing_organization: 'string',
      issue_date: 'date',
      expiry_date: 'date',
      credential_id: 'string',
      credential_url: 'string',
      participant_name: 'string'
    },
    sourceAuthority: DOCUMENT_SOURCES.MountainDriving
  },

  TransportationOfDangerousGoods: {
    documentType: 'TransportationOfDangerousGoods',
    displayName: 'Transportation Of Dangerous Goods',
    schema: {
      certification_name: 'string',
      issuing_organization: 'string',
      issue_date: 'date',
      expiry_date: 'date',
      credential_id: 'string',
      credential_url: 'string',
      participant_name: 'string',
      certificate_number: 'string'
    },
    sourceAuthority: DOCUMENT_SOURCES.TransportationOfDangerousGoods
  },

  VehicleInspections: {
    documentType: 'VehicleInspections',
    displayName: 'Vehicle Inspections',
    schema: {
      certification_name: 'string',
      issuing_organization: 'string',
      issue_date: 'date',
      expiry_date: 'date',
      credential_id: 'string',
      credential_url: 'string',
      participant_name: 'string'
    },
    sourceAuthority: DOCUMENT_SOURCES.VehicleInspections
  },

  WinterDriving: {
    documentType: 'WinterDriving',
    displayName: 'Winter Driving',
    schema: {
      certification_name: 'string',
      issuing_organization: 'string',
      issue_date: 'date',
      expiry_date: 'date',
      credential_id: 'string',
      credential_url: 'string',
      participant_name: 'string'
    },
    sourceAuthority: DOCUMENT_SOURCES.WinterDriving
  },

  WorkplaceHazardousMaterialsInformationSystem: {
    documentType: 'WorkplaceHazardousMaterialsInformationSystem',
    displayName: 'Workplace Hazardous Materials Information System',
    schema: {
      certification_name: 'string',
      issuing_organization: 'string',
      issue_date: 'date',
      expiry_date: 'date',
      credential_id: 'string',
      credential_url: 'string',
      participant_name: 'string'
    },
    sourceAuthority: DOCUMENT_SOURCES.WorkplaceHazardousMaterialsInformationSystem
  },

  DefensiveDriving: {
    documentType: 'DefensiveDriving',
    displayName: 'Defensive Driving',
    schema: {
      certification_name: 'string',
      issuing_organization: 'string',
      issue_date: 'date',
      expiry_date: 'date',
      credential_id: 'string',
      credential_url: 'string',
      participant_name: 'string'
    },
    sourceAuthority: DOCUMENT_SOURCES.DefensiveDriving
  },

  CargoSecurement: {
    documentType: 'CargoSecurement',
    displayName: 'Cargo Securement',
    schema: {
      certification_name: 'string',
      issuing_organization: 'string',
      issue_date: 'date',
      expiry_date: 'date',
      credential_id: 'string',
      credential_url: 'string',
      participant_name: 'string'
    },
    sourceAuthority: DOCUMENT_SOURCES.CargoSecurement
  },

  // ==========================================
  // DQF Documents
  // ==========================================

  Abstract: {
    documentType: 'Abstract',
    displayName: 'MVR/Abstract',
    schema: {
      license_number: 'string',
      report_generation_date: 'date',
      valid_to_date: 'date',
      issuing_authority: 'string',
      driver_name: 'string',
      date_of_birth: 'date',
      violations_count: 'number',
      accidents_count: 'number',
      license_status: 'string'
    },
    sourceAuthority: DOCUMENT_SOURCES.Abstract
  },

  PoliceClearance: {
    documentType: 'PoliceClearance',
    displayName: 'Criminal Record (Background) Check',
    schema: {
      report_generation_date: 'date',
      valid_to_date: 'date',
      issuing_authority: 'string',
      full_name: 'string',
      date_of_birth: 'date',
      reference_number: 'string',
      clearance_status: 'string'
    },
    sourceAuthority: DOCUMENT_SOURCES.PoliceClearance
  },

  PreEmploymentScreening: {
    documentType: 'PreEmploymentScreening',
    displayName: 'Pre Employment Screening',
    schema: {
      report_generation_date: 'date',
      valid_to_date: 'date',
      screening_company: 'string',
      applicant_name: 'string',
      date_of_birth: 'date',
      report_number: 'string',
      screening_result: 'string'
    },
    sourceAuthority: DOCUMENT_SOURCES.PreEmploymentScreening
  },

  EmploymentHistoryCheck: {
    documentType: 'EmploymentHistoryCheck',
    displayName: 'Employment History Check',
    schema: {
      report_generation_date: 'date',
      valid_to_date: 'date',
      verification_company: 'string',
      applicant_name: 'string',
      date_of_birth: 'date',
      report_number: 'string',
      verification_status: 'string'
    },
    sourceAuthority: DOCUMENT_SOURCES.EmploymentHistoryCheck
  },

  OntarioCvorAbstract: {
    documentType: 'OntarioCvorAbstract',
    displayName: 'Ontario CVOR Abstract',
    schema: {
      cvor_number: 'string',
      report_generation_date: 'date',
      valid_to_date: 'date',
      carrier_name: 'string',
      carrier_address: 'string',
      safety_rating: 'string',
      violations_count: 'number'
    },
    sourceAuthority: DOCUMENT_SOURCES.OntarioCvorAbstract
  },

  DrugAlcoholTest: {
    documentType: 'DrugAlcoholTest',
    displayName: 'Drug & Alcohol Test',
    schema: {
      test_date: 'date',
      test_result: 'select',
      testing_facility: 'string',
      donor_name: 'string',
      date_of_birth: 'date',
      specimen_id: 'string',
      test_type: 'string',
      collector_name: 'string'
    },
    dropdownOptions: {
      test_result: TEST_RESULTS
    },
    sourceAuthority: DOCUMENT_SOURCES.DrugAlcoholTest
  },

  RoadTest: {
    documentType: 'RoadTest',
    displayName: 'Road Test',
    schema: {
      test_date: 'date',
      test_result: 'select',
      examiner_name: 'string',
      driver_name: 'string',
      date_of_birth: 'date',
      license_class: 'select',
      test_location: 'string',
      vehicle_type: 'string'
    },
    dropdownOptions: {
      test_result: TEST_RESULTS,
      license_class: LICENSE_CLASSES_ALL
    },
    sourceAuthority: DOCUMENT_SOURCES.RoadTest
  },

  DrugAndAlcoholClearingHouseReport: {
    documentType: 'DrugAndAlcoholClearingHouseReport',
    displayName: 'Drug & Alcohol Clearing House Report',
    schema: {
      report_generation_date: 'date',
      valid_to_date: 'date',
      driver_name: 'string',
      license_number: 'string',
      date_of_birth: 'date',
      violations_found: 'boolean',
      report_number: 'string'
    },
    sourceAuthority: DOCUMENT_SOURCES.DrugAndAlcoholClearingHouseReport
  },

  WsibClearance: {
    documentType: 'WsibClearance',
    displayName: 'WSIB Clearance',
    schema: {
      clearance_number: 'string',
      issue_date: 'date',
      expiry_date: 'date',
      company_name: 'string',
      account_number: 'string',
      clearance_status: 'string'
    },
    sourceAuthority: DOCUMENT_SOURCES.WsibClearance
  },

  // ==========================================
  // Shipment Documents
  // ==========================================

  ProofOfDelivery: {
    documentType: 'ProofOfDelivery',
    displayName: 'Proof Of Delivery',
    schema: {
      delivery_date: 'date',
      recipient_name: 'string',
      shipment_id: 'string',
      signature_present: 'boolean',
      delivery_location: 'string',
      delivery_time: 'string',
      carrier_name: 'string'
    },
    sourceAuthority: DOCUMENT_SOURCES.ProofOfDelivery
  },

  BillOfLading: {
    documentType: 'BillOfLading',
    displayName: 'Bill Of Lading',
    schema: {
      bol_number: 'string',
      ship_date: 'date',
      shipper_name: 'string',
      consignee_name: 'string',
      total_weight: 'number',
      carrier_name: 'string',
      origin: 'string',
      destination: 'string',
      freight_class: 'string'
    },
    sourceAuthority: DOCUMENT_SOURCES.BillOfLading
  },

  PapsLabel: {
    documentType: 'PapsLabel',
    displayName: 'PAPS Label',
    schema: {
      paps_number: 'string',
      entry_date: 'date',
      carrier_name: 'string',
      consignee_name: 'string',
      shipper_name: 'string',
      entry_port: 'string'
    },
    sourceAuthority: DOCUMENT_SOURCES.PapsLabel
  },

  ParsLabel: {
    documentType: 'ParsLabel',
    displayName: 'PARS Label',
    schema: {
      pars_number: 'string',
      entry_date: 'date',
      carrier_name: 'string',
      consignee_name: 'string',
      shipper_name: 'string',
      entry_port: 'string'
    },
    sourceAuthority: DOCUMENT_SOURCES.ParsLabel
  },

  // ==========================================
  // Incident Documents
  // ==========================================

  PoliceReport: {
    documentType: 'PoliceReport',
    displayName: 'Police Report',
    schema: {
      report_number: 'string',
      incident_date: 'date',
      reporting_officer: 'string',
      incident_location: 'string',
      report_date: 'date',
      department_name: 'string',
      case_number: 'string'
    },
    sourceAuthority: DOCUMENT_SOURCES.PoliceReport
  },

  DriverAccidentReport: {
    documentType: 'DriverAccidentReport',
    displayName: 'Driver Accident Report',
    schema: {
      report_date: 'date',
      accident_date: 'date',
      driver_name: 'string',
      location: 'string',
      vehicle_id: 'string',
      description: 'string',
      injuries_reported: 'boolean'
    },
    sourceAuthority: DOCUMENT_SOURCES.DriverAccidentReport
  },

  RoadsideInspectionReport: {
    documentType: 'RoadsideInspectionReport',
    displayName: 'Roadside Inspection Report',
    schema: {
      inspection_date: 'date',
      inspector_name: 'string',
      location: 'string',
      vehicle_id: 'string',
      violations_found: 'boolean',
      inspection_level: 'string',
      report_number: 'string',
      carrier_name: 'string'
    },
    sourceAuthority: DOCUMENT_SOURCES.RoadsideInspectionReport
  },

  Ticket: {
    documentType: 'Ticket',
    displayName: 'Ticket',
    schema: {
      ticket_number: 'string',
      issue_date: 'date',
      violation_description: 'string',
      issuing_officer: 'string',
      location: 'string',
      fine_amount: 'number',
      court_date: 'date'
    },
    sourceAuthority: DOCUMENT_SOURCES.Ticket
  },

  TicketReceipt: {
    documentType: 'TicketReceipt',
    displayName: 'Ticket Receipt',
    schema: {
      receipt_number: 'string',
      payment_date: 'date',
      amount_paid: 'number',
      ticket_number: 'string',
      payment_method: 'string',
      transaction_id: 'string'
    },
    sourceAuthority: DOCUMENT_SOURCES.TicketReceipt
  },

  RepairInvoice: {
    documentType: 'RepairInvoice',
    displayName: 'Repair Invoice',
    schema: {
      invoice_number: 'string',
      repair_date: 'date',
      vendor_name: 'string',
      total_amount: 'number',
      vehicle_id: 'string',
      description: 'string',
      invoice_date: 'date'
    },
    sourceAuthority: DOCUMENT_SOURCES.RepairInvoice
  }
};

